import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface SplitConfig {
  sidebarSize: number;
  lastUpdated: number;
}

@Injectable({
  providedIn: 'root'
})
export class SplitStorageService {
  private readonly STORAGE_KEY = 'angular-split-config';
  private readonly DEFAULT_SIDEBAR_SIZE = 280;
  
  private configSubject = new BehaviorSubject<SplitConfig>({
    sidebarSize: this.DEFAULT_SIDEBAR_SIZE,
    lastUpdated: Date.now()
  });

  public config$: Observable<SplitConfig> = this.configSubject.asObservable();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const config: SplitConfig = JSON.parse(stored);
        // Validate the config
        if (config.sidebarSize && config.sidebarSize >= 200 && config.sidebarSize <= 400) {
          this.configSubject.next(config);
        } else {
          this.resetToDefault();
        }
      } else {
        this.resetToDefault();
      }
    } catch (error) {
      console.warn('Failed to load split config from localStorage:', error);
      this.resetToDefault();
    }
  }

  public updateSidebarSize(size: number): void {
    const config: SplitConfig = {
      sidebarSize: Math.max(200, Math.min(400, size)), // Clamp between 200-400
      lastUpdated: Date.now()
    };
    
    this.configSubject.next(config);
    this.saveToStorage(config);
  }

  public getSidebarSize(): number {
    return this.configSubject.value.sidebarSize;
  }

  public resetToDefault(): void {
    const config: SplitConfig = {
      sidebarSize: this.DEFAULT_SIDEBAR_SIZE,
      lastUpdated: Date.now()
    };
    
    this.configSubject.next(config);
    this.saveToStorage(config);
  }

  private saveToStorage(config: SplitConfig): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(config));
    } catch (error) {
      console.warn('Failed to save split config to localStorage:', error);
    }
  }

  public clearStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear split config from localStorage:', error);
    }
  }
} 